import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Phone, CreditCard, Shield, Ticket, Plus, X, Upload, 
  FileText, MapPin, AlertTriangle, BarChart3, Trash2, Download,
  User, Briefcase, Home, File, TrendingUp, Edit, Save
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { theme } = useTheme();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [performanceStats, setPerformanceStats] = useState([]);
  const [latestPerformance, setLatestPerformance] = useState(null);
  const [warningsCount, setWarningsCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Check for edit parameter in URL
  const searchParams = new URLSearchParams(window.location.search);
  const shouldOpenEdit = searchParams.get('edit') === 'true';
  
  const [editForm, setEditForm] = useState({
    matricule: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    poste: '',
    type_contrat: 'CDI',
    date_embauche: '',
    jours_max_semaine: 5,
    actif: true,
    station: ''
  });
  
  // Liste des stations disponibles
  const STATIONS = [
    { value: 'CHRONO TLS', label: 'CHRONO TLS' },
    { value: 'CHRONO LIG', label: 'CHRONO LIG' },
    { value: 'CHRONO PAU', label: 'CHRONO PAU' },
    { value: 'FEDEX', label: 'FEDEX' },
    { value: 'AMAZON DAC2', label: 'AMAZON DAC2' },
    { value: 'AUTRES', label: 'AUTRES' }
  ];
  
  const [newOutil, setNewOutil] = useState({
    type: 'telephone_pro',
    numero: '',
    commentaire: ''
  });
  
  const [newDocument, setNewDocument] = useState({
    type: 'contrat_cdi',
    nom: '',
    file: null,
    date_expiration: ''
  });
  
  const [addressForm, setAddressForm] = useState({
    rue: '',
    code_postal: '',
    ville: '',
    pays: 'France'
  });

  const documentTypes = [
    { value: 'contrat_cdi', label: 'Contrat CDI' },
    { value: 'carte_vitale', label: 'Carte Vitale' },
    { value: 'justificatif_domicile', label: 'Justificatif de domicile' },
    { value: 'permis', label: 'Permis de conduire' },
    { value: 'efficacite_conduite', label: 'Efficacité de conduite' },
    { value: 'resultats_performance', label: 'Résultats de performance' },
    { value: 'autre', label: 'Autre document' }
  ];

  useEffect(() => {
    fetchEmployee();
    fetchPerformanceStats();
    fetchWarningsCount();
  }, [id]);

  // Open edit dialog if URL has edit=true parameter
  useEffect(() => {
    if (shouldOpenEdit && employee && hasRole(['admin', 'rh'])) {
      openEditDialog();
      // Remove the query parameter from URL
      window.history.replaceState({}, '', `/employees/${id}`);
    }
  }, [employee, shouldOpenEdit]);

  const fetchEmployee = async () => {
    try {
      const response = await api.get(`/employees/${id}`);
      setEmployee(response.data);
      if (response.data.adresse) {
        setAddressForm(response.data.adresse);
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error('Erreur lors du chargement du salarié');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceStats = async () => {
    try {
      const response = await api.get(`/employees/${id}/performance`);
      setPerformanceStats(response.data || []);
      
      const latestResponse = await api.get(`/employees/${id}/performance/latest`);
      setLatestPerformance(latestResponse.data);
    } catch (error) {
      console.error('Error fetching performance:', error);
    }
  };

  const fetchWarningsCount = async () => {
    try {
      const response = await api.get(`/warnings?employee_id=${id}`);
      setWarningsCount(response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching warnings:', error);
    }
  };

  const handleAddOutil = async (e) => {
    e.preventDefault();
    try {
      const updatedOutils = [...(employee.outils || []), {
        id: Date.now().toString(),
        ...newOutil,
        date_attribution: new Date().toISOString(),
        etat: 'actif'
      }];
      
      await api.put(`/employees/${id}`, { outils: updatedOutils });
      toast.success('Outil ajouté avec succès');
      setDialogOpen(false);
      setNewOutil({ type: 'telephone_pro', numero: '', commentaire: '' });
      fetchEmployee();
    } catch (error) {
      console.error('Error adding tool:', error);
      toast.error('Erreur lors de l\'ajout de l\'outil');
    }
  };

  const handleRemoveOutil = async (outilId) => {
    try {
      const updatedOutils = employee.outils.map(o => 
        o.id === outilId ? { ...o, etat: 'rendu', date_retour: new Date().toISOString() } : o
      );
      
      await api.put(`/employees/${id}`, { outils: updatedOutils });
      toast.success('Outil marqué comme rendu');
      fetchEmployee();
    } catch (error) {
      console.error('Error removing tool:', error);
      toast.error('Erreur lors de la restitution de l\'outil');
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!newDocument.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('type', newDocument.type);
      formData.append('nom', newDocument.nom || newDocument.file.name);
      if (newDocument.date_expiration) {
        formData.append('date_expiration', newDocument.date_expiration);
      }
      
      await api.post(`/employees/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document ajouté avec succès');
      setDocumentDialogOpen(false);
      setNewDocument({ type: 'contrat_cdi', nom: '', file: null, date_expiration: '' });
      fetchEmployee();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors de l\'upload du document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await api.delete(`/employees/${id}/documents/${docId}`);
      toast.success('Document supprimé');
      fetchEmployee();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/employees/${id}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fichier_nom || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/employees/${id}`, { adresse: addressForm });
      toast.success('Adresse mise à jour');
      setAddressDialogOpen(false);
      fetchEmployee();
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openEditDialog = () => {
    // Initialize form with current employee data
    setEditForm({
      matricule: employee.matricule || '',
      nom: employee.nom || '',
      prenom: employee.prenom || '',
      email: employee.email || '',
      telephone: employee.telephone || '',
      poste: employee.poste || '',
      type_contrat: employee.type_contrat || 'CDI',
      date_embauche: employee.date_embauche ? new Date(employee.date_embauche).toISOString().split('T')[0] : '',
      jours_max_semaine: employee.jours_max_semaine || 5,
      actif: employee.actif !== false,
      station: employee.station || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        ...editForm,
        date_embauche: editForm.date_embauche ? new Date(editForm.date_embauche).toISOString() : null
      };
      
      await api.put(`/employees/${id}`, updateData);
      toast.success('Salarié modifié avec succès');
      setEditDialogOpen(false);
      fetchEmployee();
    } catch (error) {
      console.error('Error updating employee:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la modification';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Salarié supprimé avec succès');
      navigate('/employees');
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Erreur lors de la suppression du salarié');
    }
  };

  const getOutilIcon = (type) => {
    switch(type) {
      case 'telephone_pro': return Phone;
      case 'carte_total': return CreditCard;
      case 'badge_vigie': return Shield;
      case 'badge_telepage': return Ticket;
      default: return Shield;
    }
  };

  const getOutilLabel = (type) => {
    const labels = {
      'telephone_pro': 'Téléphone Pro',
      'carte_total': 'Carte Total',
      'badge_vigie': 'Badge Vigie',
      'badge_telepage': 'Badge Télepage'
    };
    return labels[type] || type;
  };

  const getDocumentTypeLabel = (type) => {
    const doc = documentTypes.find(d => d.value === type);
    return doc?.label || type;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    if (score >= 50) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={cn(
          "relative w-12 h-12",
        )}>
          <div className={cn("w-12 h-12 border-4 rounded-full", theme === 'dark' ? 'border-zinc-800' : 'border-gray-200')}></div>
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className={cn(
        "text-center py-12",
        theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
      )}>
        <p>Salarié non trouvé</p>
      </div>
    );
  }

  const outilsActifs = employee.outils?.filter(o => o.etat === 'actif') || [];
  const outilsRendus = employee.outils?.filter(o => o.etat === 'rendu') || [];
  const employeeDocuments = employee.employee_documents || [];

  // Tabs configuration
  const tabs = [
    { id: 'general', label: 'Général', icon: User },
    { id: 'adresse', label: 'Adresse', icon: Home },
    { id: 'documents', label: 'Documents', icon: File },
    { id: 'outils', label: 'Outils', icon: Briefcase },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'suppression', label: 'Suppression', icon: Trash2, danger: true }
  ];

  return (
    <div className="space-y-4 px-1 md:px-0 pb-24 md:pb-0" data-testid="employee-detail-page">
      {/* Header - Single line with name and buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/employees')}
          data-testid="back-button"
          className={cn(
            "h-9 w-9 p-0 shrink-0",
            theme === 'dark' ? 'border-white/10' : ''
          )}
        >
          <ArrowLeft size={16} />
        </Button>
        
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <h2 className={cn(
            "text-lg md:text-xl font-semibold truncate",
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {employee.prenom} {employee.nom}
          </h2>
          <span className={cn(
            "text-xs md:text-sm hidden sm:inline",
            theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'
          )}>
            •
          </span>
          <span className={cn(
            "text-xs md:text-sm truncate hidden sm:inline",
            theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
          )}>
            {employee.poste}
          </span>
        </div>
        
        {/* Stats badges - hidden on very small screens */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {latestPerformance && (
            <div className={cn(
              "px-2 py-1 rounded-lg text-xs",
              latestPerformance.score_global >= 70 
                ? 'bg-green-500/20 text-green-500' 
                : 'bg-orange-500/20 text-orange-500'
            )}>
              <span className="font-bold">{latestPerformance.score_global}</span>/100
            </div>
          )}
          {warningsCount > 0 && (
            <div className="px-2 py-1 rounded-lg text-xs bg-red-500/20 text-red-500">
              <span className="font-bold">{warningsCount}</span> avert.
            </div>
          )}
        </div>

        {hasRole(['admin', 'rh']) && (
          <Button
            onClick={openEditDialog}
            variant="outline"
            size="sm"
            data-testid="edit-employee-button"
            className={cn(
              "h-9 w-9 p-0 shrink-0",
              theme === 'dark' ? 'border-white/10' : ''
            )}
          >
            <Edit size={16} />
          </Button>
        )}
      </div>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className={cn(
          "max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
        )}>
          <DialogHeader>
            <DialogTitle className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Modifier le salarié</DialogTitle>
            <DialogDescription className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'}>
              Modifiez les informations du salarié ci-dessous
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateEmployee} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-matricule" className={theme === 'dark' ? 'text-zinc-300' : ''}>Matricule</Label>
                <Input
                  id="edit-matricule"
                  value={editForm.matricule}
                  onChange={(e) => setEditForm({...editForm, matricule: e.target.value})}
                  placeholder="MAT001"
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
              <div>
                <Label htmlFor="edit-actif" className={theme === 'dark' ? 'text-zinc-300' : ''}>Statut</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    id="edit-actif"
                    checked={editForm.actif}
                    onCheckedChange={(checked) => setEditForm({...editForm, actif: checked})}
                  />
                  <span className={editForm.actif ? 'text-green-600' : 'text-red-600'}>
                    {editForm.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-prenom" className={theme === 'dark' ? 'text-zinc-300' : ''}>Prénom *</Label>
                <Input
                  id="edit-prenom"
                  value={editForm.prenom}
                  onChange={(e) => setEditForm({...editForm, prenom: e.target.value})}
                  required
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
              <div>
                <Label htmlFor="edit-nom" className={theme === 'dark' ? 'text-zinc-300' : ''}>Nom *</Label>
                <Input
                  id="edit-nom"
                  value={editForm.nom}
                  onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
                  required
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-email" className={theme === 'dark' ? 'text-zinc-300' : ''}>Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  required
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
              <div>
                <Label htmlFor="edit-telephone" className={theme === 'dark' ? 'text-zinc-300' : ''}>Téléphone</Label>
                <Input
                  id="edit-telephone"
                  value={editForm.telephone}
                  onChange={(e) => setEditForm({...editForm, telephone: e.target.value})}
                  placeholder="06 12 34 56 78"
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-poste" className={theme === 'dark' ? 'text-zinc-300' : ''}>Poste *</Label>
                <Input
                  id="edit-poste"
                  value={editForm.poste}
                  onChange={(e) => setEditForm({...editForm, poste: e.target.value})}
                  required
                  placeholder="Chauffeur poids lourd"
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
              <div>
                <Label htmlFor="edit-type-contrat" className={theme === 'dark' ? 'text-zinc-300' : ''}>Type de contrat</Label>
                <Select 
                  value={editForm.type_contrat} 
                  onValueChange={(value) => setEditForm({...editForm, type_contrat: value})}
                >
                  <SelectTrigger id="edit-type-contrat" className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Interim">Intérim</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                    <SelectItem value="Alternance">Alternance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex flex-col items-center sm:col-span-2">
              <Label htmlFor="edit-date-embauche" className={cn('text-center', theme === 'dark' ? 'text-zinc-300' : '')}>Date d'embauche</Label>
              <Input
                id="edit-date-embauche"
                type="date"
                value={editForm.date_embauche}
                onChange={(e) => setEditForm({...editForm, date_embauche: e.target.value})}
                className={cn(
                  "rounded-lg mt-1",
                  theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : ''
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-jours-max" className={theme === 'dark' ? 'text-zinc-300' : ''}>Jours max/semaine</Label>
                <Input
                  id="edit-jours-max"
                  type="number"
                  min="1"
                  max="7"
                  value={editForm.jours_max_semaine}
                  onChange={(e) => setEditForm({...editForm, jours_max_semaine: parseInt(e.target.value) || 5})}
                  className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}
                />
              </div>
              <div>
                <Label htmlFor="edit-station" className={theme === 'dark' ? 'text-zinc-300' : ''}>Station</Label>
                <Select 
                  value={editForm.station || ''} 
                  onValueChange={(value) => setEditForm({...editForm, station: value})}
                >
                  <SelectTrigger id="edit-station" className={cn(
                    "rounded-lg",
                    theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : ''
                  )}>
                    <SelectValue placeholder="Sélectionner une station" />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : ''}>
                    {STATIONS.map(station => (
                      <SelectItem key={station.value} value={station.value}>{station.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter className="pt-4 flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className={theme === 'dark' ? 'border-white/10' : ''}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tabs - Mobile dropdown / Desktop tabs */}
      <div className="w-full">
        {/* Mobile: Dropdown selector */}
        <div className="md:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className={cn(
              "w-full h-10 px-3 rounded-lg border text-sm font-medium appearance-none",
              theme === 'dark'
                ? 'bg-zinc-800/50 border-white/10 text-white'
                : 'bg-white border-gray-200 text-gray-900'
            )}
          >
            {tabs.map(tab => (
              <option key={tab.id} value={tab.id}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop: Horizontal tabs */}
        <div className={cn(
          "hidden md:flex items-center gap-1 p-1 rounded-xl border w-fit",
          theme === 'dark' ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-100 border-gray-200'
        )}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                  activeTab === tab.id
                    ? tab.danger 
                      ? 'bg-red-500 text-white' 
                      : 'bg-red-500 text-white'
                    : theme === 'dark'
                      ? tab.danger ? 'text-red-400 hover:bg-red-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      : tab.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-600 hover:bg-white'
                )}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content: Général */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className={cn(
            "rounded-xl border p-4",
            theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
          )}>
            <h3 className={cn(
              "text-base font-semibold mb-4",
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>Informations générales</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Matricule</span>
                <p className={cn("font-mono text-lg", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  {employee.matricule || 'N/A'}
                </p>
              </div>
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Email</span>
                <p className={cn(theme === 'dark' ? 'text-white' : 'text-gray-900')}>{employee.email}</p>
              </div>
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Téléphone</span>
                <p className={cn(theme === 'dark' ? 'text-white' : 'text-gray-900')}>{employee.telephone || 'N/A'}</p>
              </div>
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Type de contrat</span>
                <p className={cn(theme === 'dark' ? 'text-white' : 'text-gray-900')}>{employee.type_contrat}</p>
              </div>
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Date d'embauche</span>
                <p className={cn(theme === 'dark' ? 'text-white' : 'text-gray-900')}>{formatDate(employee.date_embauche)}</p>
              </div>
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Station</span>
                <p className={cn(theme === 'dark' ? 'text-white' : 'text-gray-900')}>{employee.station || 'Non définie'}</p>
              </div>
              <div>
                <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Statut</span>
                <p>
                  <span className={cn(
                    "px-2 py-0.5 rounded text-xs font-medium",
                    employee.actif 
                      ? 'bg-green-500/20 text-green-500' 
                      : theme === 'dark' ? 'bg-zinc-700 text-zinc-400' : 'bg-gray-200 text-gray-600'
                  )}>
                    {employee.actif ? 'Actif' : 'Inactif'}
                  </span>
                </p>
              </div>
              
              {/* Responsable Toggle */}
              {hasRole(['admin', 'rh']) && (
                <div className={cn(
                  "pt-3 mt-3 border-t",
                  theme === 'dark' ? 'border-white/10' : 'border-gray-200'
                )}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={cn("text-sm font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                        Responsable d'équipe
                      </span>
                      <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                        Apparaît dans le planning
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await api.put(`/employees/${id}`, { is_responsable: !employee.is_responsable });
                          toast.success(employee.is_responsable ? 'Retiré des responsables' : 'Ajouté aux responsables');
                          fetchEmployee();
                        } catch (error) {
                          toast.error('Erreur lors de la mise à jour');
                        }
                      }}
                      className={cn(
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                        employee.is_responsable ? 'bg-red-500' : theme === 'dark' ? 'bg-zinc-700' : 'bg-gray-300'
                      )}
                      data-testid="toggle-responsable"
                    >
                      <span
                        className={cn(
                          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                          employee.is_responsable ? 'translate-x-6' : 'translate-x-1'
                        )}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Liens rapides - Corrigés pour dark/light mode */}
          <div className={cn(
            "rounded-xl border p-4",
            theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
          )}>
            <h3 className={cn(
              "text-base font-semibold mb-4",
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>Liens rapides</h3>
            <div className="space-y-3">
              <Link 
                to={`/warnings?employee_id=${id}`}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-colors",
                  theme === 'dark' 
                    ? 'bg-zinc-800/50 hover:bg-zinc-700/50' 
                    : 'bg-gray-50 hover:bg-gray-100'
                )}
              >
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <AlertTriangle size={18} className="text-orange-500" />
                </div>
                <div>
                  <p className={cn("font-medium text-sm", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    Avertissements
                  </p>
                  <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                    {warningsCount} avertissement(s)
                  </p>
                </div>
              </Link>
              
              <button 
                onClick={() => setActiveTab('performance')}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-colors w-full text-left",
                  theme === 'dark' 
                    ? 'bg-zinc-800/50 hover:bg-zinc-700/50' 
                    : 'bg-gray-50 hover:bg-gray-100'
                )}
              >
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <BarChart3 size={18} className="text-blue-500" />
                </div>
                <div>
                  <p className={cn("font-medium text-sm", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    Statistiques de livraison
                  </p>
                  <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                    {latestPerformance ? `Score: ${latestPerformance.score_global}/100` : 'Aucune donnée'}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Tab: Adresse */}
      {activeTab === 'adresse' && (
          <div className={cn(
            "rounded-xl border p-4 max-w-2xl",
            theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
          )}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={cn(
                "text-base font-semibold flex items-center gap-2",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                <MapPin size={18} />
                Adresse postale
              </h3>
              {hasRole(['admin', 'rh']) && (
                <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="edit-address-button" variant="outline" className={theme === 'dark' ? 'border-white/10' : ''}>
                      Modifier
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={theme === 'dark' ? 'bg-zinc-900 border-white/10' : ''}>
                    <DialogHeader>
                      <DialogTitle className={theme === 'dark' ? 'text-white' : ''}>Modifier l'adresse</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateAddress} className="space-y-4">
                      <div>
                        <Label htmlFor="rue" className={theme === 'dark' ? 'text-zinc-300' : ''}>Rue / Numéro</Label>
                        <Input
                          id="rue"
                          value={addressForm.rue}
                          onChange={(e) => setAddressForm({...addressForm, rue: e.target.value})}
                          placeholder="123 Rue de Paris"
                          className={theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : ''}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="code_postal" className={theme === 'dark' ? 'text-zinc-300' : ''}>Code postal</Label>
                          <Input
                            id="code_postal"
                            value={addressForm.code_postal}
                            onChange={(e) => setAddressForm({...addressForm, code_postal: e.target.value})}
                            placeholder="75001"
                            className={theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : ''}
                          />
                        </div>
                        <div>
                          <Label htmlFor="ville" className={theme === 'dark' ? 'text-zinc-300' : ''}>Ville</Label>
                          <Input
                            id="ville"
                            value={addressForm.ville}
                            onChange={(e) => setAddressForm({...addressForm, ville: e.target.value})}
                            placeholder="Paris"
                            className={theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : ''}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="pays" className={theme === 'dark' ? 'text-zinc-300' : ''}>Pays</Label>
                        <Input
                          id="pays"
                          value={addressForm.pays}
                          onChange={(e) => setAddressForm({...addressForm, pays: e.target.value})}
                          placeholder="France"
                          className={theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : ''}
                        />
                      </div>
                      <Button type="submit" variant="brand" className="w-full">Enregistrer</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            
            {employee.adresse ? (
              <div className="space-y-2 text-sm">
                <p className={cn("text-base", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  {employee.adresse.rue || 'Non renseigné'}
                </p>
                <p className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  {employee.adresse.code_postal} {employee.adresse.ville}
                </p>
                <p className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'}>
                  {employee.adresse.pays}
                </p>
              </div>
            ) : (
              <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                Aucune adresse renseignée
              </p>
            )}
          </div>
      )}

        {/* Tab: Documents */}
      {activeTab === 'documents' && (
        <div className={cn(
          "rounded-xl border p-4",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
        )}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={cn(
              "text-base font-semibold flex items-center gap-2",
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>
              <FileText size={18} />
              Documents
            </h3>
            {hasRole(['admin', 'rh']) && (
              <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-document-button" variant="outline" className={theme === 'dark' ? 'border-white/10' : ''}>
                    <Upload size={14} className="mr-1" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className={theme === 'dark' ? 'bg-zinc-900 border-white/10' : ''}>
                  <DialogHeader>
                    <DialogTitle className={theme === 'dark' ? 'text-white' : ''}>Ajouter un document</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUploadDocument} className="space-y-4">
                    <div>
                      <Label className={theme === 'dark' ? 'text-zinc-300' : ''}>Type de document</Label>
                      <Select value={newDocument.type} onValueChange={(value) => setNewDocument({...newDocument, type: value})}>
                        <SelectTrigger className={theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : ''}>
                          {documentTypes.map(dt => (
                            <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={theme === 'dark' ? 'text-zinc-300' : ''}>Nom du document</Label>
                      <Input
                        value={newDocument.nom}
                        onChange={(e) => setNewDocument({...newDocument, nom: e.target.value})}
                        placeholder="Ex: Contrat CDI 2024"
                        className={theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={theme === 'dark' ? 'text-zinc-300' : ''}>Fichier</Label>
                      <Input
                        type="file"
                        onChange={(e) => setNewDocument({...newDocument, file: e.target.files[0]})}
                        className={cn("cursor-pointer", theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white file:text-zinc-400' : '')}
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <Label className={cn('text-center', theme === 'dark' ? 'text-zinc-300' : '')}>Date d'expiration (optionnel)</Label>
                      <Input
                        type="date"
                        value={newDocument.date_expiration}
                        onChange={(e) => setNewDocument({...newDocument, date_expiration: e.target.value})}
                        className={cn('mt-1', theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : '')}
                      />
                    </div>
                    <Button type="submit" variant="brand" className="w-full">Télécharger</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {employeeDocuments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {employeeDocuments.map((doc) => (
                <div key={doc.id} className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  theme === 'dark' ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-50 border-gray-200'
                )}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-2 bg-blue-500/20 rounded-lg shrink-0">
                      <FileText size={16} className="text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <p className={cn("font-medium text-sm truncate", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                        {doc.nom || doc.fichier_nom}
                      </p>
                      <p className={cn("text-xs truncate", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                        {getDocumentTypeLabel(doc.type)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc)} className={theme === 'dark' ? 'text-zinc-400 hover:text-white' : ''}>
                      <Download size={14} />
                    </Button>
                    {hasRole(['admin', 'rh']) && (
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)} className="text-red-500 hover:text-red-400">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={cn("text-sm text-center py-8", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
              Aucun document
            </p>
          )}
        </div>
      )}

        {/* Tab: Outils */}
        {activeTab === 'outils' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Outils attribués</h3>
              {hasRole(['admin', 'rh', 'responsable']) && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-tool-button">
                      <Plus size={16} className="mr-1" />
                      Ajouter
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Attribuer un outil</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddOutil} className="space-y-4">
                      <div>
                        <Label htmlFor="type">Type d'outil</Label>
                        <Select
                          value={newOutil.type}
                          onValueChange={(value) => setNewOutil({...newOutil, type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="telephone_pro">Téléphone Pro</SelectItem>
                            <SelectItem value="carte_total">Carte Total</SelectItem>
                            <SelectItem value="badge_vigie">Badge Vigie</SelectItem>
                            <SelectItem value="badge_telepage">Badge Télepage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="numero">Numéro / Identifiant</Label>
                        <Input
                          id="numero"
                          value={newOutil.numero}
                          onChange={(e) => setNewOutil({...newOutil, numero: e.target.value})}
                          placeholder="Ex: 06 XX XX XX XX, 1234567890"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="commentaire">Commentaire (optionnel)</Label>
                        <Textarea
                          id="commentaire"
                          value={newOutil.commentaire}
                          onChange={(e) => setNewOutil({...newOutil, commentaire: e.target.value})}
                          rows={2}
                          placeholder="Notes supplémentaires..."
                        />
                      </div>
                      <Button type="submit" className="w-full">Attribuer</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {outilsActifs.length > 0 ? (
              <div className="space-y-3">
                {outilsActifs.map((outil) => {
                  const Icon = getOutilIcon(outil.type);
                  return (
                    <div 
                      key={outil.id} 
                      className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      data-testid={`tool-${outil.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Icon size={18} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{getOutilLabel(outil.type)}</p>
                          <p className="text-xs text-gray-600 mt-1">{outil.numero}</p>
                          {outil.commentaire && (
                            <p className="text-xs text-gray-500 mt-1">{outil.commentaire}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Attribué le {formatDate(outil.date_attribution)}
                          </p>
                        </div>
                      </div>
                      {hasRole(['admin', 'rh', 'responsable']) && (
                        <button
                          onClick={() => handleRemoveOutil(outil.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          title="Marquer comme rendu"
                        >
                          <X size={16} className="text-gray-600" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Aucun outil attribué</p>
            )}

            {outilsRendus.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-600 mb-3">Outils rendus</h4>
                <div className="space-y-2">
                  {outilsRendus.map((outil) => {
                    const Icon = getOutilIcon(outil.type);
                    return (
                      <div key={outil.id} className="flex items-center gap-2 text-xs text-gray-500">
                        <Icon size={14} />
                        <span>{getOutilLabel(outil.type)} - {outil.numero}</span>
                        <span className="text-gray-400">
                          (rendu le {formatDate(outil.date_retour)})
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
              )}

        {/* Tab: Performance */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* Score global */}
            {latestPerformance ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 size={20} />
                  Score global de performance
                </h3>
                
                <div className="flex items-center gap-6 mb-6">
                  <div className={`text-5xl font-bold ${getScoreColor(latestPerformance.score_global)} px-6 py-4 rounded-xl`}>
                    {latestPerformance.score_global}
                    <span className="text-2xl">/100</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Dernière mise à jour: {formatDate(latestPerformance.imported_at)}</p>
                    <p>Période: {formatDate(latestPerformance.periode_debut)} - {formatDate(latestPerformance.periode_fin)}</p>
                    {latestPerformance.fichier_source && (
                      <p>Source: {latestPerformance.fichier_source}</p>
                    )}
                  </div>
                </div>
                
                {/* Detailed metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">SPMO (Vitesse globale)</p>
                    <p className="text-2xl font-bold">{latestPerformance.spmo?.toFixed(1) || 0}%</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">LSP (Limite vitesse)</p>
                    <p className="text-2xl font-bold">{latestPerformance.lsp?.toFixed(1) || 0}%</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">SPMD (Détail vitesse)</p>
                    <p className="text-2xl font-bold">{latestPerformance.spmd?.toFixed(1) || 0}%</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">POD (Points)</p>
                    <p className="text-2xl font-bold">{latestPerformance.pod || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">CC (Code conduite)</p>
                    <p className="text-2xl font-bold">{latestPerformance.cc || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">CE (Code événement)</p>
                    <p className="text-2xl font-bold">{latestPerformance.ce || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">COP (Coefficient)</p>
                    <p className="text-2xl font-bold">{latestPerformance.cop || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">DDR / DSC</p>
                    <p className="text-2xl font-bold">{latestPerformance.ddr?.toFixed(1) || 0}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm text-center">
                <BarChart3 size={48} className="mx-auto mb-3 text-slate-300" />
                <p className="text-gray-500">Aucune donnée de performance</p>
                <p className="text-sm text-gray-400">Importez un fichier Excel pour voir les statistiques</p>
              </div>
            )}
            
            {/* Historical data */}
            {performanceStats.length > 1 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Historique des performances</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Période</th>
                        <th className="text-right py-2">Score</th>
                        <th className="text-right py-2">SPMO</th>
                        <th className="text-right py-2">LSP</th>
                        <th className="text-right py-2">SPMD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceStats.map((stat, idx) => (
                        <tr key={stat.id || idx} className="border-b">
                          <td className="py-2">{formatDate(stat.periode_fin)}</td>
                          <td className={`text-right py-2 font-bold ${getScoreColor(stat.score_global)}`}>
                            {stat.score_global}
                          </td>
                          <td className="text-right py-2">{stat.spmo?.toFixed(1)}%</td>
                          <td className="text-right py-2">{stat.lsp?.toFixed(1)}%</td>
                          <td className="text-right py-2">{stat.spmd?.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
              )}

        {/* Tab: Suppression */}
        {activeTab === 'suppression' && (
          <div className="bg-white rounded-lg border border-red-200 p-6 shadow-sm max-w-xl">
            <h3 className="text-lg font-semibold mb-4 text-red-600 flex items-center gap-2">
              <Trash2 size={20} />
              Zone de danger
            </h3>
            <p className="text-gray-600 mb-6">
              La suppression d'un salarié est irréversible. Toutes les données associées 
              (documents, outils, statistiques) seront définitivement perdues.
            </p>
            
            {hasRole(['admin', 'rh']) ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full" data-testid="delete-employee-button">
                    <Trash2 size={18} className="mr-2" />
                    Supprimer définitivement ce salarié
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer <strong>{employee.prenom} {employee.nom}</strong> ?
                      Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteEmployee}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <p className="text-gray-500 text-sm bg-gray-50 p-4 rounded-lg">
                Seuls les administrateurs et RH peuvent supprimer un salarié.
              </p>
            )}
          </div>
              )}
    </div>
  );
}
